import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  type?: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://dhivishaarchitects.com";
const DEFAULT_TITLE = "Dhivisha Architects — Registered Architect in Kumbakonam & Delta District, Tamil Nadu";
const DEFAULT_DESC = "COA-registered architecture & interior design firm in Kumbakonam, Cauvery Delta region. Residential, commercial, cultural projects across Thanjavur, Mayiladuthurai & Nagapattinam. 350+ projects, 300+ clients. Call +91 99948 49332.";

const SECTION_NAMES: Record<string, string> = {
  about: "About Us",
  leaders: "Leadership",
  philosophy: "Philosophy",
  projects: "Projects",
  services: "Services",
  blog: "Blog",
  contact: "Contact",
  faq: "FAQ",
  "privacy-policy": "Privacy Policy",
  "terms-of-service": "Terms of Service",
};

const buildBreadcrumbs = (path: string, title?: string) => {
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return null;
  const items = [{ name: "Home", item: `${SITE_URL}/` }];
  parts.forEach((p, i) => {
    const isLast = i === parts.length - 1;
    const url = `${SITE_URL}/${parts.slice(0, i + 1).join("/")}`;
    const name = isLast && parts.length > 1 && title ? title : SECTION_NAMES[p] || p;
    items.push({ name, item: p === "leaders" ? `${SITE_URL}/about` : url });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, ...it })),
  };
};

const SEOHead = ({
  title,
  description,
  path = "/",
  type = "website",
  image,
  noindex = false,
  jsonLd,
}: SEOHeadProps) => {
  const fullTitle = title ? `${title} | Dhivisha Architects` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const url = `${SITE_URL}${path}`;
  const ogImage = image || `${SITE_URL}/og-image.png`;
  const breadcrumbs = buildBreadcrumbs(path, title);
  const extra = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />

      <meta name="author" content="Dhivisha Architects and Interiors" />
      <meta name="geo.region" content="IN-TN" />
      <meta name="geo.placename" content="Kumbakonam" />

      {breadcrumbs && <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>}
      {extra.map((d, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(d)}</script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
